import React, { useState } from "react";
import { Delete, X } from "lucide-react";

/**
 * Big touch-friendly numeric pad dialog. Calls onConfirm(value) when user confirms.
 */
const NumpadDialog = ({ open, title, subtitle, initial = "", onClose, onConfirm, confirmLabel = "Confirm", confirmColor = "#228EBF", footer = null }) => {
  const [val, setVal] = useState(initial);

  React.useEffect(() => {
    if (open) setVal(initial || "");
  }, [open, initial]);

  if (!open) return null;

  const press = (n) => setVal((v) => (v + n).slice(0, 6));
  const back = () => setVal((v) => v.slice(0, -1));
  const dot = () => setVal((v) => (v.includes(".") ? v : (v || "0") + "."));

  const num = parseFloat(val);
  const valid = !Number.isNaN(num) && num > 0;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="numpad-overlay"
      onClick={onClose}
    >
      <div
        className="nb-card p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="numpad-dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs tracking-wide font-semibold text-[#607087]">
              {subtitle}
            </div>
            <h3 className="font-display text-2xl leading-tight mt-1">{title}</h3>
          </div>
          <button
            data-testid="numpad-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
            aria-label="close"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        <div
          data-testid="numpad-display"
          className="mt-4 nb-card-sm p-4 text-right font-mono-num font-bold text-5xl min-h-[80px] flex items-center justify-end"
          style={{ background: "#F4F4F0" }}
        >
          {val || "0"}
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button
              key={n}
              data-testid={`numpad-${n}`}
              onClick={() => press(String(n))}
              className="nb-btn bg-white py-4 text-3xl font-display"
            >
              {n}
            </button>
          ))}
          <button
            data-testid="numpad-dot"
            onClick={dot}
            className="nb-btn bg-white py-4 text-3xl font-display"
          >
            .
          </button>
          <button
            data-testid="numpad-0"
            onClick={() => press("0")}
            className="nb-btn bg-white py-4 text-3xl font-display"
          >
            0
          </button>
          <button
            data-testid="numpad-back"
            onClick={back}
            className="nb-btn bg-[#001740] text-white py-4 flex items-center justify-center"
          >
            <Delete size={26} strokeWidth={2.5} />
          </button>
        </div>

        {footer && <div className="mt-4">{footer}</div>}

        <button
          data-testid="numpad-confirm"
          disabled={!valid}
          onClick={() => valid && onConfirm(num)}
          className="nb-btn w-full mt-4 py-4 text-2xl"
          style={{
            background: valid ? confirmColor : "#ccc",
            color: "#fff",
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  );
};

export default NumpadDialog;
